import { CantileverButton } from 'components/CantileverButton'
import { TextInput } from 'components/form/inputs/TextInput'
import { isPartialNumericInput } from 'features/timeSeries/utils/numericInput'
import { X } from 'lucide-react'
import React, { useLayoutEffect, useRef, useState } from 'react'
import styled from 'styled-components'

const StyledX = styled(X)`
  height: 1rem;
  width: 1rem;
  color: ${({ theme }) => theme.forecast.danger};
`

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  position: relative;

  input::selection {
    background: ${({ theme }) => theme.forecast.primary}60;
  }
`

interface EditableRangeInputProps {
  /** The committed custom value; undefined shows the placeholder (auto). */
  value?: number
  placeholder: string
  /**
   * Called with the raw committed string on blur/Enter (always a valid
   * number), or with '' when the field is cleared back to auto.
   */
  onCommit: (value: string) => void
  ariaLabel?: string
  /** Disables editing (and hides the clear button) without unmounting. */
  disabled?: boolean
}

/**
 * Numeric min/max field with the pens-table commit semantics, shared by every
 * y-scale editing surface:
 * - local state, committed on blur/Enter; Escape reverts
 * - only numeric keystrokes accepted (digits, minus, decimal point)
 * - focusing the field selects all its text
 * - the clear button commits immediately, reverting the bound to auto
 */
export const EditableRangeInput: React.FC<EditableRangeInputProps> = ({
  value,
  placeholder,
  onCommit,
  ariaLabel,
  disabled = false,
}) => {
  const committedText = value !== undefined ? value.toString() : ''
  const [localValue, setLocalValue] = useState<string>(committedText)
  // Enter/Escape blur the field; this keeps the follow-up blur event from
  // committing again (Enter) or committing the reverted text (Escape).
  const skipBlurCommitRef = useRef(false)

  // Layout effect (not useEffect) so the revert-then-resync below never paints
  // an intermediate frame: an accepted commit re-renders with the new value and
  // this sync overwrites the reverted text before the browser paints.
  useLayoutEffect(() => {
    setLocalValue(committedText)
  }, [committedText])

  const commit = (inputValue: string) => {
    // A disabled field must never commit — events can still arrive (e.g. a
    // blur fired by the disabling itself).
    if (disabled) {
      return
    }
    if (inputValue === '') {
      // Only meaningful when there was a value to clear.
      if (value !== undefined) {
        onCommit('')
      }
      return
    }
    const parsed = parseFloat(inputValue)
    if (Number.isFinite(parsed)) {
      onCommit(inputValue)
      // The handler may REJECT the commit (e.g. a min above the current max is
      // reverted rather than inverting the range). Falling back to the
      // committed text keeps the field honest either way: on accept the value
      // prop changes and the layout effect above resyncs to the new text; on
      // reject the field shows the previous value instead of a number that was
      // never applied.
      setLocalValue(committedText)
    } else {
      // A leftover partial state ("-", ".") never commits — revert.
      setLocalValue(committedText)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value
    // Keystroke-level filter (also covers paste): only strings that could
    // still become a valid number are accepted.
    if (isPartialNumericInput(next)) {
      setLocalValue(next)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      skipBlurCommitRef.current = true
      commit(e.currentTarget.value)
      e.currentTarget.blur()
    } else if (e.key === 'Escape') {
      skipBlurCommitRef.current = true
      setLocalValue(committedText)
      e.currentTarget.blur()
    }
  }

  const handleBlur = () => {
    if (skipBlurCommitRef.current) {
      skipBlurCommitRef.current = false
      return
    }
    commit(localValue)
  }

  return (
    <InputWrapper>
      <TextInput
        type='input'
        value={localValue}
        placeholder={placeholder}
        aria-label={ariaLabel}
        disabled={disabled}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
          e.currentTarget.select()
        }
        mode='edit'
      />
      {localValue && !disabled && (
        <CantileverButton
          iconOnly
          ghost
          aria-label='Clear (auto scale)'
          // Without this, clicking Clear while an uncommitted draft sits in
          // the input first BLURS the input — the blur commits the draft, and
          // on a previously-auto field the button can unmount before its
          // click ever fires, committing the draft instead of clearing it.
          // Suppressing the default mousedown keeps focus (and the button)
          // in place so the click handler below runs.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            setLocalValue('')
            commit('')
          }}>
          <StyledX />
        </CantileverButton>
      )}
    </InputWrapper>
  )
}
